/**
 * A resumed board's move budget. Rescues add to the budget as they're used
 * (`buyExtraMoves`, `requestExtraMoves`), and an ad's grant depends on the
 * order of rescues, so the saved budget is kept but bounded: never below the
 * pristine budget, never above it plus the largest grant for each rescue used.
 * Shared with the Daily's resume (`enterDaily`), which used to reset the budget
 * to the pristine one and so dropped the moves a player had paid for.
 */
export function resumedBudget(saved, pristine, adsUsed, bought, caps) {
    if (pristine === undefined)
        return undefined;
    const max = pristine + adsUsed * caps.adGrantMax + bought * caps.boughtGrant;
    if (typeof saved !== 'number' || !Number.isFinite(saved))
        return max;
    return Math.min(max, Math.max(pristine, Math.floor(saved)));
}
/** Stable JSON: object keys sorted, so a save round-trip can't change the text. */
function canonical(value) {
    if (Array.isArray(value))
        return `[${value.map(canonical).join(',')}]`;
    if (value !== null && typeof value === 'object') {
        const entries = Object.entries(value)
            .filter(([, v]) => v !== undefined)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
        return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
    }
    return JSON.stringify(value);
}
const isCount = (v, max) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;
/** A block without its position: what a move can't change. */
function identity(block) {
    const rest = { ...block };
    delete rest.position;
    return canonical(rest);
}
/** The saved block list, if every block is one of `pristine`'s, moved but unchanged. */
function savedBlocks(saved, pristine) {
    if (!Array.isArray(saved) || saved.length === 0)
        return null;
    const byId = new Map(pristine.blocks.map((b) => [b.id, b]));
    const seen = new Set();
    const size = pristine.puzzle.gridSize;
    for (const raw of saved) {
        if (raw === null || typeof raw !== 'object')
            return null;
        const block = raw;
        const original = byId.get(block.id);
        if (!original || seen.has(block.id))
            return null;
        seen.add(block.id);
        const p = block.position;
        if (!p || !isCount(p.row, size - 1) || !isCount(p.col, size - 1))
            return null;
        if (identity(block) !== identity(original))
            return null;
    }
    return saved;
}
/**
 * The board to play: the saved attempt when it fits `pristine` (the same
 * track puzzle, rebuilt), else `null` (start fresh).
 */
export function resumeTrackBoard(saved, ordinal, pristine, caps) {
    if (!saved || saved.ordinal !== ordinal)
        return null;
    const s = saved.state;
    if (!s || canonical(s.puzzle) !== canonical(pristine.puzzle))
        return null;
    const blocks = savedBlocks(s.blocks, pristine);
    if (!blocks || !isCount(s.moveCount, 100000))
        return null;
    // A board with no move made is the pristine one: nothing to resume.
    if (s.moveCount === 0 && blocks.length === pristine.blocks.length)
        return null;
    const clamp = (v, max) => (isCount(v, max) ? v : max);
    // Absent means none were used; a bad value counts as all of them used.
    const ads = s.movesAdsUsed === undefined ? 0 : clamp(s.movesAdsUsed, caps.maxAds);
    const bought = s.movesBought === undefined ? 0 : clamp(s.movesBought, caps.maxBought);
    const moveBudget = resumedBudget(s.moveBudget, pristine.moveBudget, ads, bought, caps);
    return {
        state: {
            ...pristine,
            blocks,
            moveCount: s.moveCount,
            hintsUsed: isCount(s.hintsUsed, 100000) ? s.hintsUsed : 0,
            undoStack: [],
            ...(moveBudget !== undefined ? { moveBudget } : {}),
            movesAdsUsed: ads,
            movesBought: bought,
            // An open, unpaid Wild Gates or Master Key is closed: closing is free
            // (Modules 90–91), and whether it was a free-use ticket isn't saved.
        },
        powerupsUsed: { ...(saved.powerupsUsed ?? {}) },
    };
}
